import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import PlayArrow from "@material-ui/icons/PlayArrow";
import Pause from "@material-ui/icons/Pause";
import Mic from "@material-ui/icons/Mic";

const BARS = 36;
const SPEEDS = [1, 1.5, 2];

const useStyles = makeStyles((theme) => {
  const dark = theme.mode === "dark" || theme.palette.type === "dark";
  const accent = dark ? "#53bdeb" : "#00a884";
  const idle = dark ? "rgba(255,255,255,0.32)" : "rgba(17,27,33,0.28)";
  return {
    root: { display: "flex", alignItems: "center", gap: 8, width: 300, maxWidth: "100%", padding: "6px 4px 14px" },
    avatarWrap: { position: "relative", flex: "none", width: 46, height: 46 },
    avatar: { width: 46, height: 46, borderRadius: "50%", objectFit: "cover", background: idle },
    mic: { position: "absolute", left: -4, bottom: -2, fontSize: 18, color: accent },
    play: {
      flex: "none", width: 34, height: 34, border: "none", background: "transparent", padding: 0,
      color: dark ? "#aebac1" : "#54656f", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
      "& svg": { fontSize: 34 },
    },
    track: { position: "relative", flex: 1, minWidth: 0 },
    bars: { display: "flex", alignItems: "center", gap: 2, height: 30, cursor: "pointer", touchAction: "none" },
    bar: { flex: 1, minWidth: 2, borderRadius: 2, background: idle, transition: "background 120ms linear" },
    barOn: { background: accent },
    knob: {
      position: "absolute", top: "50%", width: 12, height: 12, marginTop: -6, marginLeft: -6,
      borderRadius: "50%", background: accent, pointerEvents: "none",
    },
    meta: { position: "absolute", left: 0, right: 0, top: 32, display: "flex", justifyContent: "space-between", fontSize: 11, color: dark ? "rgba(233,237,239,0.6)" : "#667781", fontVariantNumeric: "tabular-nums" },
    speed: {
      border: "none", borderRadius: 12, padding: "1px 7px", fontSize: 11, fontWeight: 700, cursor: "pointer",
      background: dark ? "rgba(255,255,255,0.14)" : "rgba(17,27,33,0.12)", color: dark ? "#e9edef" : "#54656f",
    },
  };
});

const clock = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return "0:00";
  const s = Math.floor(secs);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** Alturas estables por mensaje: la onda no cambia al volver a renderizar. */
export const waveHeights = (seed, count = BARS) => {
  let h = 2166136261;
  for (const ch of String(seed || "audio")) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return Array.from({ length: count }, (_, i) => {
    h = Math.imul(h ^ (i + 1), 2654435761) >>> 0;
    const wave = 0.55 + 0.45 * Math.sin(i / 2.3);
    return Math.round(18 + ((h % 1000) / 1000) * 62 * wave);
  });
};

/** Nota de voz al estilo WhatsApp: play, onda con progreso, duración, velocidad y foto. */
const VoiceNotePlayer = ({ src, seed, avatarUrl }) => {
  const classes = useStyles();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const heights = useMemo(() => waveHeights(seed || src), [seed, src]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onTime = () => setCurrent(audio.currentTime);
    const onMeta = () => {
      // Los .ogg/.webm de WhatsApp a veces reportan Infinity hasta que se recorren.
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnd = () => { setPlaying(false); setCurrent(0); };
    const onPause = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("play", onPlay);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("play", onPlay);
    };
  }, [src]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      // Solo un audio a la vez, como en el teléfono.
      document.querySelectorAll("audio[data-voice-note]").forEach((el) => { if (el !== audio) el.pause(); });
      audio.play().catch(() => setPlaying(false));
    } else audio.pause();
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    setCurrent(audio.currentTime);
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const progress = duration ? Math.min(1, current / duration) : 0;
  const lit = Math.round(progress * heights.length);

  return (
    <div className={classes.root}>
      <audio ref={audioRef} src={src} preload="metadata" data-voice-note="1" />
      {avatarUrl ? (
        <div className={classes.avatarWrap}>
          <img className={classes.avatar} src={avatarUrl} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
          <Mic className={classes.mic} />
        </div>
      ) : null}
      <button type="button" className={classes.play} onClick={toggle} aria-label={playing ? "Pausar" : "Reproducir"}>
        {playing ? <Pause /> : <PlayArrow />}
      </button>
      <div className={classes.track}>
        <div className={classes.bars} onClick={seek} role="slider" aria-valuemin={0} aria-valuemax={Math.round(duration)} aria-valuenow={Math.round(current)} tabIndex={0}>
          {heights.map((h, i) => (
            <span key={i} className={`${classes.bar} ${i < lit ? classes.barOn : ""}`} style={{ height: `${h}%` }} />
          ))}
        </div>
        {(playing || current > 0) && <span className={classes.knob} style={{ left: `${progress * 100}%`, top: 15 }} />}
        <div className={classes.meta}>
          <span>{clock(playing || current > 0 ? current : duration)}</span>
          {(playing || speed !== 1) && (
            <button type="button" className={classes.speed} onClick={cycleSpeed}>{speed}×</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceNotePlayer;
